import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import BrandMark from "../components/BrandMark";
import LanguageSwitcher from "../components/LanguageSwitcher";
import PasswordInput from "../components/PasswordInput";
import { api, formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../i18n/LanguageContext";

/**
 * Consumes a one-time admin-issued recovery link.
 *
 *   /reset-password/:token  →  GET /api/auth/reset-password/:token to validate
 *                              POST /api/auth/reset-password to consume
 *
 * The page is intentionally minimal — no portal nav, no membership chrome —
 * because the user has not authenticated yet.
 */
export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();

  const [state, setState] = useState({ status: "loading", email: "", reason: "" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/auth/reset-password/${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (data.valid) {
          setState({ status: "ready", email: data.email || "", reason: "" });
        } else {
          setState({ status: "invalid", email: "", reason: data.reason || "not_found" });
        }
      } catch (e) {
        if (cancelled) return;
        setState({ status: "invalid", email: "", reason: "network" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(t("reset.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message || t("reset.error_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen br-aurora flex flex-col items-center justify-center px-6 py-12">
      <div className="absolute top-5 right-6">
        <LanguageSwitcher />
      </div>
      <div className="max-w-[480px] w-full br-card px-8 py-10 br-fade-up" data-testid="reset-password-card">
        <div className="flex items-center gap-4 mb-6">
          <BrandMark size={44} />
          <div>
            <div className="br-overline">{t("reset.overline")}</div>
            <div className="font-serif-display italic text-[1.2rem] text-[color:var(--br-parchment)] mt-1 leading-none">
              {t("reset.title_a")} <span className="text-[color:var(--br-bronze-soft)]">{t("reset.title_em")}</span>
            </div>
          </div>
        </div>

        {state.status === "loading" && (
          <p
            className="text-[color:var(--br-text-dim)] text-[0.9rem]"
            data-testid="reset-password-loading"
          >
            {t("reset.loading")}
          </p>
        )}

        {state.status === "invalid" && (
          <div data-testid="reset-password-invalid">
            <h2 className="font-serif-display text-[1.6rem] leading-tight">
              {t("reset.invalid_title")}
            </h2>
            <p className="mt-4 text-[0.95rem] text-[color:var(--br-text)] leading-relaxed">
              {state.reason === "expired"
                ? t("reset.invalid_expired")
                : state.reason === "used"
                  ? t("reset.invalid_used")
                  : t("reset.invalid_generic")}
            </p>
            <Link
              to="/login"
              className="br-btn-outline mt-6 inline-block"
              data-testid="reset-password-back-to-login"
            >
              {t("reset.back_to_login")}
            </Link>
          </div>
        )}

        {state.status === "ready" && !done && (
          <>
            <h2 className="font-serif-display text-[1.65rem] leading-tight">
              {t("reset.heading_a")} <em>{t("reset.heading_em")}</em>
              {t("reset.heading_b")}
            </h2>
            <p className="mt-3 text-[0.9rem] text-[color:var(--br-text-dim)] leading-relaxed">
              {t("reset.intro_for_email", { email: state.email })}
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-password-form">
              <label className="block">
                <span className="br-overline">{t("reset.new_password")}</span>
                <div className="mt-2">
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    minLength={10}
                    data-testid="reset-password-new-input"
                  />
                </div>
              </label>
              <label className="block">
                <span className="br-overline">{t("reset.confirm_password")}</span>
                <div className="mt-2">
                  <PasswordInput
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                    minLength={10}
                    data-testid="reset-password-confirm-input"
                  />
                </div>
              </label>
              <p
                className="text-[0.72rem] text-[color:var(--br-text-dim)] leading-relaxed"
                data-testid="reset-password-policy"
              >
                {t("reset.policy_hint")}
              </p>
              {error && (
                <div
                  className="text-[0.85rem] py-2 px-3 border-l-2"
                  style={{ borderColor: "var(--br-red)", color: "var(--br-red)" }}
                  data-testid="reset-password-error"
                >
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting || !password || !confirm}
                className="br-btn-primary w-full"
                data-testid="reset-password-submit"
              >
                {submitting ? t("reset.submitting") : t("reset.submit")}
              </button>
            </form>
          </>
        )}

        {state.status === "ready" && done && (
          <div data-testid="reset-password-done">
            <h2 className="font-serif-display text-[1.6rem] leading-tight">
              {t("reset.done_title")}
            </h2>
            <p className="mt-3 text-[0.95rem] text-[color:var(--br-text)] leading-relaxed">
              {t("reset.done_p1")}
            </p>
            <button
              onClick={() => navigate("/login")}
              className="br-btn-primary mt-6 w-full"
              data-testid="reset-password-go-login"
            >
              {t("reset.go_login")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
